// @flow
import * as React from 'react';
import { shuffle } from 'lodash';
import Button from 'components/atoms/button/Button';
import { MEDIA_QUERIES } from 'javascripts/constants';
import type { Image } from './types';
import Card from './Card';

type Props = {
  defaultImage: string,
  images: Array<Image>,
};

type State = {
  images: Array<Image>,
  openCards: Array<Image>,
  solvedCards: Array<Image>,
  tries: number,
  isDesktop: boolean,
  description: ?string
};

export default class MemoryGame extends React.Component<Props, State> {
  state = {
    images: shuffle(this.props.images.concat(this.props.images)).map((image, index) => ({
      ...image,
      id: index,
    })),
    openCards: [],
    solvedCards: [],
    tries: 0,
    isDesktop: false,
    description: null,
  };

  componentDidMount() {
    this.getScreenSize();
    window.addEventListener('resize', this.getScreenSize);
  }


  render() {
    const { defaultImage } = this.props;
    const { images, tries } = this.state;

    return (
      <div className="memory-game__inner">
        <div className="memory-game__control">
          <div className="memory-game__tries">Versuche: <span className="memory-game__tries-number">{tries}</span></div>
          <Button
            style='secondary'
            onClick={this.handleNewTryClick}>
              Neuer Versuch
          </Button>
        </div>
        <ul className="memory-game__list">
          {images.map(image => (
            <li className="memory-game__item" key={image.id}>
              <Card
                defaultImage={defaultImage}
                image={image}
                onCardClick={this.handleOnCardClick}
                visible={this.isCardVisible(image)}
                isDesktop={this.state.isDesktop}
              />
            </li>
          ))}
        </ul>
        {!this.state.isDesktop && this.state.description &&
          <div className="memory-game__description">{this.state.description}</div>
        }
      </div>
    );
  }

  isCardVisible = (image: Image) => {
    const { solvedCards, openCards } = this.state;

    return solvedCards.includes(image) || openCards.includes(image);
  };

  handleNewTryClick = (event: SyntheticEvent<HTMLButtonElement>) => {
    const $target: EventTarget = event.currentTarget;
    if ($target instanceof HTMLInputElement) {
      $target.blur();
    }
    this.setState(({ images }) => ({
      images: shuffle(images),
      solvedCards: [],
      openCards: [],
      tries: 0,
    }));
  };

  addToOpenCards = (image: Image) => {
    this.setState(({ openCards }) => ({
      openCards: openCards ? [...openCards, image] : [image],
      description: image.description,
    }));
  };

  closeOpenCards = () => {
    this.setState(({ tries }) => ({
      openCards: [],
      tries: tries + 1,
      description: null,
    }));
  };

  addOpenCardsToSolvedCards = (image: Image) => {
    this.setState(({ solvedCards, tries, openCards }) => ({
      solvedCards: [...solvedCards, image, openCards[0]],
      openCards: [],
      tries: tries + 1,
      description: image.description,
    }));
  };

  handleOnCardClick = (image: Image) => {
    const { openCards, solvedCards } = this.state;

    // First click on a hidden Card
    if (openCards.length === 0 && !solvedCards.includes(image)) {
      this.addToOpenCards(image);
      return;
    }

    // Second click on the first Card
    if (openCards.length === 1 && openCards[0] === image) {
      return;
    }

    // Second click on an already solved Card
    if (openCards.length === 1 && solvedCards.includes(image)) {
      this.setState(() => ({
        description: image.description,
      }));
      return;
    }

    // Second click on the matching hidden Card
    if (openCards.length === 1 && openCards[0].url === image.url) {
      this.addOpenCardsToSolvedCards(image);
      return;
    }

    // Second click on the wrong hidden Card
    if (openCards.length === 1 && openCards[0] !== image) {
      this.addToOpenCards(image);
      return;
    }

    // Click on any card when there are already two flipped
    if (openCards.length === 2) {
      this.closeOpenCards();
    }

    if (solvedCards.includes(image)) {
      this.setState(() => ({
        description: image.description,
      }));
    }
  };

  getScreenSize = () => {
    this.setState(() => ({
      isDesktop: window.matchMedia(MEDIA_QUERIES.l).matches,
    }));
  }
}
